import { h } from "preact";
import { useEffect, useState } from "preact/hooks";
import { getStellaBIOCalibrationReport } from "../api";
import { getOrderInfo, getProductTypeByGtin, getSerialNumberInfo, Product } from "../api/product-api";
import { serialNumberSearchExecs } from "../data/serialNumberSearchExecs";
import { useCommand, useForceUpdate } from "../hooks";
import { findSerialNumberByRegex, parseToDateAndFormatIfExists, SearchInStringExec } from "../utils";
import { Alert } from "./Alert";
import { Chip } from "./Chip";
import { ScanPackQR } from "./ScanPackQR";
import { Snackbar, SnackbarShowCommand } from "./Snackbar";

const gtinRegex = /^(\d{14}|\d{13}|\d{12}|\d{8})$/m;

function checkIfAlreadyInAnotherOrder(orderId: number, product: Partial<Product>){
    if(!product.manufacturingOrderRevisionProducts) {
        return {
            result: true,
            orderId: 0
        };
    }

    const zeroRevision = product.manufacturingOrderRevisionProducts.find(v => v.archivedRevision === 0)

    if (!zeroRevision) {
        return {
            result: false
        };
    }

    return {
        result: zeroRevision.manufacturingOrderRevision.manufacturingOrder.id !== orderId,
        orderId: zeroRevision.manufacturingOrderRevision.manufacturingOrder.id
    };
}

export function OrderComposer(props: {
    id?: string | number,
    edit: boolean,
    commitTitle?: string,
    orderTypes: string[],
    serialRegexes: SearchInStringExec[],
    onCommit: (orderName: string, description: string, products: { serialNumber: string, quantity: number }[], otherData: object) => Promise<void> | void
}) {
    const [elementList, setElementList] = useState(null as { serialNumber: string, quantity: number }[] | null);
    const [elementListInfo, setElementListInfo] = useState(null as {
        [key: string]: Partial<Product> & { qualityControlPass?: string, mac?: string, exists?: boolean }
    } | null);
    const [orderName, setOrderName] = useState("");
    const [orderCreationError, setOrderCreationError] = useState("");
    const [inputSerialNumber, setInputSerialNumber] = useState("");
    const [orderCustomName, setOrderCustomName] = useState("");
    const [orderAsanaLink, setOrderAsanaLink] = useState("");
    const [orderJiraLink, setOrderJiraLink] = useState("");
    const [orderNotes, setOrderNotes] = useState("");
    const [partialScan, setPartialScan] = useState(null as string[] | null);
    const [overrideOrderTypes, setOverrideOrderTypes] = useState(null as string[] | null);
    const [overrideSerialRegexes, setOverrideSerialRegexes] = useState(null as SearchInStringExec[] | null);
    const showSnackbarCommand = useCommand<SnackbarShowCommand>();
    const forceUpdate = useForceUpdate();

    const orderId = typeof props.id === "number" ? props.id : (props.id ? parseInt(props.id) : NaN);

    const addProduct = (serialNumber: string) => {
        if (!elementList) {
            return;
        }

        const existingProduct = elementList.find(v => v.serialNumber === serialNumber);

        if (existingProduct) {
            const productInfo = elementListInfo ? elementListInfo[serialNumber] : null;
            const serialNumberType = productInfo?.productTypeRevision?.productType?.serialNumberType;

            if (!serialNumberType) {
                showSnackbarCommand.run(`Element o numerze seryjnym '${serialNumber}' jest już na liście`, "warning", "warning")
                return;
            }

            if (serialNumberType.representsSingleProduct) {
                showSnackbarCommand.run(`Element o numerze '${serialNumber}' jest już na liście i może wsytąpić na niej tylko raz`, "warning", "warning")
                return;
            }

            existingProduct.quantity++;
            showSnackbarCommand.run(`Dodano kolejną sztukę elementu o numerze '${serialNumber}'`, "info", "info");
            forceUpdate();
            return;
        }

            showSnackbarCommand.run(`Dodano element o numerze '${serialNumber}'`, "success", "check", 2000);
            setElementList(elementList.concat({ serialNumber, quantity: 1 }));
    }

    useEffect(() => {
        if (isNaN(orderId)) {
            setElementList([]);
            return;
        }

        getOrderInfo(orderId).then(async (order) => {
            order.manufacturingOrderRevisions.sort((a, b) => b.version - a.version);
            const data = order.manufacturingOrderRevisions[0];

            const orderSearchExecs = serialNumberSearchExecs.find(v => v.orderTypes.indexOf(order.name) >= 0);

            setOrderName(order.name);
            setOverrideOrderTypes(orderSearchExecs?.orderTypes ?? null);
            setOverrideSerialRegexes(orderSearchExecs?.orderedExecs ?? null);
            setOrderCustomName(data.description ?? "");
            setOrderAsanaLink(data.otherData?.asanaLink ?? "");
            setOrderJiraLink(data.otherData?.jiraLink ?? "");
            setOrderNotes(data.otherData?.notes ?? "");
            setElementList(data.manufacturingOrderRevisionProducts!.map((item) => ({ serialNumber: item.product.serialNumber, quantity: item.quantity })));
        });
    }, [props.id]);

    useEffect(() => {
        if (!elementList) {
            setElementListInfo(null);
            return;
        }

        getSerialNumberInfo(elementList.map(v => v.serialNumber)).then(async (data) => {
        // if (!elementList.every(v => keys.indexOf(v) >= 0)) {
            //     // missing
            // }

            const output = {} as { [key: string]: (Product & { qualityControlPass?: string, exists?: boolean }) | { exists: false } };

            for (let i = 0; i < elementList.length; i++) {
                output[elementList[i].serialNumber] = { exists: false };
            }

            for (let i = 0; i < data.length; i++) {
                const element = data[i] as Required<Product> & { qualityControlPass?: string };

                output[element.serialNumber] = element;
                output[element.serialNumber].exists = true;

                if (element.productTypeRevision.productType!.name === "StellaBIO") {
                    const response = await getStellaBIOCalibrationReport(element.serialNumber);

                    if (!response.ok) {
                        element.qualityControlPass = "none";
                    }
                    else {
                        const data = await response.json();
                        element.qualityControlPass = !!data.stella.calibrationPass ? "pass" : "failed";
                    }
                }
            }

            setElementListInfo(output);
        });
    }, [elementList]);

    if (!elementList || !elementListInfo) {
        return <div style="display: flex; flex-direction: column; justify-content: center; align-items: center; width: 100%; height: 100%">
            <div>Pobieranie danych...</div>
            <div class="loader"></div>
        </div>;
    }

    const allowCommitCheck = orderName
        && elementListInfo
        && (props.edit || elementList.length)
        && elementList.every(item => elementListInfo[item.serialNumber]
            && elementListInfo[item.serialNumber].exists
            && !checkIfAlreadyInAnotherOrder(orderId, elementListInfo[item.serialNumber])
            && elementListInfo[item.serialNumber].qualityControlPass !== undefined ? elementListInfo[item.serialNumber].qualityControlPass === "pass" : true
        );

    return <div class="order-composer-component">
        <div style="display: grid; grid-template-columns: 1fr 1fr; column-gap: 10px; align-items: flex-start;">
            <div style="display: inline-grid; grid-template-columns: auto auto; row-gap: 5px; column-gap: 10px; align-items: center; justify-content: flex-start;">
                <span>Rodzaj zestawienia:</span>
                {props.edit ? <input class="order-name" readOnly value={orderName}/>
                    : <select class="order-name" value={orderName} onChange={(e) => setOrderName(e.currentTarget.value)}>
                    {(overrideOrderTypes ?? props.orderTypes).map(v => <option value={v}>{v}</option>)}
                </select>}
                <span>Nazwa:</span>
                <input value={orderCustomName} onChange={(e) => setOrderCustomName(e.currentTarget.value)}/>
                <span>Link do Asana:</span>
                <input value={orderAsanaLink} onChange={(e) => setOrderAsanaLink(e.currentTarget.value)}/>
                <span>Link do Jira:</span>
                <input value={orderJiraLink} onChange={(e) => setOrderJiraLink(e.currentTarget.value)}/>
            </div>
            <div style="display: flex; flex-direction: row;">
                <div>Notatki: </div>
                <textarea style="margin-left: 10px; min-height: 100px; max-width: 100%; flex: 1 1 0; resize: none;" value={orderNotes} onChange={(e) => setOrderNotes(e.currentTarget.value)}/>
            </div>
        </div>
        <div style="border-top: 1px solid rgba(255, 255, 255, 0.14); margin-top: 10px; margin-bottom: 10px;"></div>
        <ScanPackQR onRead={(data) => {
            const text = JSON.parse(data) as string;

            if (partialScan) {
                addProduct(partialScan.concat(text).join("-"));
                setPartialScan(null);
                return;
            }

            const serial = findSerialNumberByRegex(text, overrideSerialRegexes ?? props.serialRegexes);

            setPartialScan(null);

            if (!serial) {
                return;
            }

            if (serial.match(gtinRegex)) {
                getProductTypeByGtin(serial).then((v) => {
                    if (!v) {
                        addProduct(serial);
                        return;
                    }

                    if (v.gtin && (v.serialNumberType?.name === "gtin-lot" || v.serialNumberType?.name === "gtin-lot-8-date")) {
                        setPartialScan([v.gtin])
                    }
                }).catch(() => {
                    addProduct(serial);
                });

                return;
            }

            addProduct(serial);
        }}/>
        <div style="margin-top: 10px">
            <span>Dodaj po numerze seryjnym:</span>
            <input style="margin-left: 10px" value={inputSerialNumber} onChange={(e) => setInputSerialNumber(e.currentTarget.value)}/>
            <button style="margin-left: 10px" type="button" onClick={(e) => {
                inputSerialNumber.split(",").map(v => v.trim()).filter(i => i.length > 0).forEach(v => addProduct(v));
            }}>Dodaj</button>
        </div>
        {partialScan ? <div style="margin-top: 10px">
            <Alert type="info">Kontynuowanie skanowania: {partialScan.join("-")}</Alert>
        </div> : null}
        <div class="element-list paper-2">
            <h2>Elementy</h2>
            <div class="content">
                <div class="header">Lp.</div>
                <div class="header">Typ elementu</div>
                <div class="header">Numer seryjny</div>
                <div class="header">Opis</div>
                <div class="header">Ilość</div>
                <div class="header">Rejestrujący</div>
                <div class="header">Data rejestracji</div>
                <div class="header">Status</div>
                <div class="header">Usuń</div>
                {elementList.map((item, i) => {
                    const element = elementListInfo[item.serialNumber];
                    const anotherCheck = element && element.exists ? checkIfAlreadyInAnotherOrder(orderId, element) : { result: false };

                    return [
                        <div>{i + 1}</div>,
                        <div>{element && element.productTypeRevision && element.productTypeRevision.productType ? element.productTypeRevision.productType.brand : null}</div>,
                        <div>
                            <span>{item.serialNumber}</span>
                            {!element ? null
                                : element.qualityControlPass === undefined ? null
                                : element.qualityControlPass === "none" ? <Chip type="warning" tooltipType="accent" tooltipPosition="top" class="qc" icon="report" tooltip="Urządzenia nie zostało poddane kontroli jakości">Kontrola jakości</Chip>
                                : element.qualityControlPass === "pass" ? <Chip type="success" tooltipType="accent" tooltipPosition="top" class="qc" icon="assignment_turned_in" tooltip="Urządzenia spełnia kontrolę jakości">Kontrola jakości</Chip>
                                : <Chip type="error" class="qc" tooltipType="accent" tooltipPosition="top" icon="report" tooltip="Urządzenia nie spełnia kontroli jakości">Kontrola jakości</Chip>}
                        </div>,
                        <div>{element ? element.description : null}</div>,
                        <div>{element ? item.quantity : null}</div>,
                        <div>{element ? element.creator?.email : null}</div>,
                        <div>{element ? parseToDateAndFormatIfExists(element.createdAt) : null}</div>,
                        <div class="status">
                            {!element ? <button class="icon r26 info tooltip-accent tooltip-top" data-tooltip="Pobieranie informacji z bazy danych...">info</button>
                                : !element.exists ? <button class="icon r26 error tooltip-accent tooltip-top" data-tooltip="Brak informacji o numerze seryjnym w bazie danych">error</button>
                                : anotherCheck.result ? <button class="icon r26 error tooltip-accent tooltip-top tooltip-end" data-tooltip={`Numer seryjny został już przypisany do innego zestawienia ${anotherCheck.orderId ? `(o identyfikatorze: ${anotherCheck.orderId})` : ""}`}>error</button> :
                                <button class="icon r26 error success tooltip-accent tooltip-top" data-tooltip="Numer seryjny jest poprawny">check_circle</button>}
                        </div>,
                        <div class="remove">
                            <button type="button" class="flat icon error tooltip-top" data-tooltip="Usuwa pozycje z listy" onClick={() => {
                                if (elementList[i].quantity > 1) {
                                    elementList[i].quantity--;
                                    forceUpdate();
                                    return;
                                }

                                elementList.splice(i, 1);
                                setElementList([...elementList]);
                            }}>close</button>
                        </div>
                    ];
                })}
            </div>
        </div>
        <div>
            <button type="button" class={allowCommitCheck ? "" : "disabled"} onClick={async () => {
                let data;

                try {
                    data = await props.onCommit(orderName, orderCustomName, elementList, { notes: orderNotes, asanaLink: orderAsanaLink, jiraLink: orderJiraLink });
                }
                catch (ex) {
                    if (ex instanceof Error) {
                        setOrderCreationError(ex.message);
                    }
                    return;
                }
            }}>{props.commitTitle ?? "Zastosuj zmiany"}</button>
            <button type="button" class="error cancel" onClick={() => history.back()}>Anuluj</button>
            {orderCreationError ? <Alert type="error">{orderCreationError}</Alert> : null}
        </div>
        <Snackbar doShow={showSnackbarCommand}/>
    </div>;
}
